// ─── RoomTabs.jsx ────────────────────────────────────────────────────────────
// The room tabs used by BOTH Moodboard and Materials (they share one room
// list), with adding and removing rooms built in.
//
//   const removal = useRoomRemoval({ rooms, setRooms, selectedRoom, setSelectedRoom,
//                                    onRoomRemoved: (room) => …drop its photos… });
//   <RoomTabStrip rooms={rooms} selectedRoom={selectedRoom} onSelectRoom={setSelectedRoom}
//                 onAddRoom={handleAddRoom} onRemoveRoom={removal.requestRemove}
//                 checkingId={removal.checkingId} />
//   {removal.dialog}

import { useState } from 'react';
import Box from '@mui/material/Box';

import {
  ScrollableTabStrip, AddCategoryChip, RemoveButton, RemoveConfirmDialog,
  removableSx, plural,
} from './ShortlistShared3';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';
import { useToast } from '../../../shared/components/status';
import { GREEN, CHARCOAL, labelMono } from '../../../theme/tokens2';

const LAST_ROOM = 'You need at least one room.';

// ─── RoomTabStrip ─────────────────────────────────────────────────────────────
export function RoomTabStrip({ rooms, selectedRoom, onSelectRoom, onAddRoom, onRemoveRoom, checkingId = null }) {
  const onlyOne = rooms.length <= 1;
  return (
    <ScrollableTabStrip>
      <Box role="tablist" aria-label="Rooms" sx={{ display: 'flex', alignItems: 'center' }}>
        {rooms.map((room) => {
          const isActive = room.room_name === selectedRoom;
          return (
            <Box key={room.id} sx={removableSx(isActive)}>
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-keyshortcuts={onRemoveRoom ? 'Delete' : undefined}
                onClick={() => onSelectRoom(room.room_name)}
                onKeyDown={onRemoveRoom ? (e) => {
                  if (e.key === 'Delete' || e.key === 'Backspace') {
                    e.preventDefault();
                    if (!onlyOne) onRemoveRoom(room);
                  }
                } : undefined}
                sx={{
                  display: 'flex', alignItems: 'center',
                  pl: '14px', pr: onRemoveRoom ? '2px' : '14px', py: '9px',
                  ...labelMono, fontSize: '9px', fontWeight: 700,
                  color: isActive ? GREEN : CHARCOAL,
                  opacity: isActive ? 1 : 0.6,
                  background: 'none', border: 'none',
                  borderBottom: `2px solid ${isActive ? GREEN : 'transparent'}`,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  transition: 'color 0.15s, border-color 0.15s, opacity 0.15s',
                  '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '-2px' },
                }}
              >
                {room.room_name}
              </Box>
              {onRemoveRoom && (
                <Box sx={{ mr: '6px' }}>
                  <RemoveButton
                    label={`Remove ${room.room_name}`}
                    onClick={() => onRemoveRoom(room)}
                    disabledReason={onlyOne ? LAST_ROOM : undefined}
                    busy={checkingId === room.id}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <AddCategoryChip onAdd={onAddRoom} label="Add room" placeholder="Room name…" />
    </ScrollableTabStrip>
  );
}

// ─── useRoomRemoval ───────────────────────────────────────────────────────────
// Everything that happens after the × is pressed:
//   1. Ask the server what's in the room (GET /api/rooms/{id}/summary).
//   2. Empty → hide the tab now and show "Kitchen removed · Undo" for
//      6 seconds. The room is only really deleted when that message closes
//      without Undo being pressed.
//   3. Not empty → the confirm box with the real counts. Removed only when
//      the customer presses "Remove room".
// After removing, the tab to the left is selected, so the page never goes blank.
// onRoomRemoved(room): lets the page drop that room's photos or materials.
export function useRoomRemoval({ rooms, setRooms, selectedRoom, setSelectedRoom, onRoomRemoved }) {
  const toast = useToast();
  const [pending,    setPending]    = useState(null); // { room, summary } while the confirm box is open
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState('');
  const [checkingId, setCheckingId] = useState(null);

  // The room that should be selected once `room` is gone: the one to its
  // left, or the new first room.
  const neighbourOf = (room) => {
    const index = rooms.findIndex((r) => r.id === room.id);
    const rest  = rooms.filter((r) => r.id !== room.id);
    return (rest[Math.max(0, index - 1)] || rest[0])?.room_name ?? '';
  };

  const hide = (room) => {
    if (selectedRoom === room.room_name) setSelectedRoom(neighbourOf(room));
    setRooms((prev) => prev.filter((r) => r.id !== room.id));
  };

  const putBack = (room, index, select) => {
    setRooms((prev) => {
      if (prev.some((r) => r.id === room.id)) return prev;
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, room);
      return next;
    });
    if (select) setSelectedRoom(room.room_name);
  };

  const removeWithUndo = (room) => {
    const index       = rooms.findIndex((r) => r.id === room.id);
    const wasSelected = selectedRoom === room.room_name;
    const name        = room.room_name;
    hide(room);
    toast.show({
      message: `${name} removed`,
      severity: 'success',
      actionLabel: 'Undo',
      duration: 6000,
      onAction: () => putBack(room, index, wasSelected),
      // keepalive: the delete still goes through if the page is closed now
      onExpire: () => {
        apiFetch(`/api/rooms/${room.id}`, { method: 'DELETE', keepalive: true }).catch(() => {
          putBack(room, index, false);
          toast.error(`We couldn't remove ${name}, so it's back in your rooms.`);
        });
      },
    });
  };

  const requestRemove = async (room) => {
    if (rooms.length <= 1 || checkingId) return;
    setCheckingId(room.id);
    let summary;
    try {
      summary = await apiFetch(`/api/rooms/${room.id}/summary`);
    } catch (err) {
      toast.error(`We couldn't check what's in ${room.room_name}. Please try again.`);
      return;
    } finally {
      setCheckingId(null);
    }
    if (summary.lastRoom) {
      toast.info(LAST_ROOM);
    } else if (summary.empty) {
      removeWithUndo(room);
    } else {
      setError('');
      setPending({ room, summary });
    }
  };

  const confirmRemove = async () => {
    const { room } = pending;
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/api/rooms/${room.id}`, { method: 'DELETE' });
      hide(room);
      onRoomRemoved?.(room);
      setPending(null);
      toast.success(`${room.room_name} removed`);
    } catch (err) {
      setError(err.status === 409
        ? `${LAST_ROOM} Add another room first if you'd like to remove this one.`
        : friendlyMessage(err, `We couldn't remove ${room.room_name}. Please try again.`));
    } finally {
      setBusy(false);
    }
  };

  // ── The confirm box's wording, from the server's counts ──
  let dialog = null;
  if (pending) {
    const { room, summary: s } = pending;
    const items = [];
    if (s.photos > 0) items.push(plural(s.photos, 'inspiration photo'));
    if (s.savedMaterials > 0) {
      items.push(plural(s.savedMaterials, 'saved material')
        + (s.samplesMarked > 0 ? ` (${s.samplesMarked} marked for samples)` : ''));
    }
    const warnings = [];
    if (s.photosSourcing > 0) {
      warnings.push(`Our team is still finding lookalikes for ${s.photosSourcing} of these photos. That work stops too.`);
    }
    if (s.samplesMarked > 0) {
      warnings.push(`Your samples request will lose ${plural(s.samplesMarked, 'item')}.`);
    }
    const notes = ['Rooms are shared by Moodboard and Materials, so it goes from both.'];
    if (s.savedFurnitureFromPhotos > 0) {
      notes.push(`${plural(s.savedFurnitureFromPhotos, 'furnishing')} you saved from these photos will stay on Fittings & Furnishings.`);
    }

    dialog = (
      <RemoveConfirmDialog
        open
        title={`Remove ${room.room_name}?`}
        intro="This also removes everything saved in this room:"
        items={items}
        warnings={warnings}
        notes={notes}
        confirmLabel="Remove room"
        busy={busy}
        error={error}
        onCancel={() => setPending(null)}
        onConfirm={confirmRemove}
      />
    );
  }

  return { requestRemove, checkingId, dialog };
}
